export interface ToolTraceEvent {
  toolName: string;
  displayName: string;
  status: "RUNNING" | "SUCCESS" | "FAILED" | string;
  durationMs?: number | null;
}

type StreamCallback = (value: string) => void;
type ToolCallback = (value: ToolTraceEvent) => void;
type EventCallback = (event: string, payload: any) => void;

const readEventStream = async (
  stream: ReadableStream<Uint8Array> | null,
  onEvent: EventCallback
) => {
  if (!stream) throw new Error("Agent 响应为空");
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const consumeBlock = (block: string) => {
    const lines = block.split(/\r?\n/);
    const event = lines.find((line) => line.startsWith("event:"))?.slice(6).trim() || "message";
    const data = lines.filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).trim()).join("\n");
    if (!data) return;
    let payload: any;
    try { payload = JSON.parse(data); }
    catch { throw new Error("Agent 返回的数据格式异常"); }
    onEvent(event, payload);
  };

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() || "";
    blocks.forEach(consumeBlock);
    if (done) break;
  }
  if (buffer.trim()) consumeBlock(buffer);
};

const asToolTrace = (payload: any): ToolTraceEvent | null => {
  if (!payload || typeof payload.toolName !== "string") return null;
  return {
    toolName: payload.toolName,
    displayName: typeof payload.displayName === "string" ? payload.displayName : payload.toolName,
    status: typeof payload.status === "string" ? payload.status : "RUNNING",
    durationMs: typeof payload.durationMs === "number" ? payload.durationMs : null,
  };
};

export const consumeAgentEventStream = async (
  stream: ReadableStream<Uint8Array> | null,
  onAnswer: StreamCallback,
  onProgress: StreamCallback = () => undefined,
  onTool: ToolCallback = () => undefined
) => {
  let answered = false;
  await readEventStream(stream, (event, payload) => {
    if (event === "progress" && typeof payload.stage === "string") onProgress(payload.stage);
    if (event === "tool") {
      const trace = asToolTrace(payload);
      if (trace) onTool(trace);
    }
    if (event === "message" && typeof payload.text === "string") {
      answered = true;
      onAnswer(payload.text);
    }
    if (event === "error") throw new Error(payload.text || "Agent 请求失败");
  });
  if (!answered) throw new Error("Agent 未返回有效回复，请重试");
};

export const consumeInterviewEventStream = async <T>(
  stream: ReadableStream<Uint8Array> | null,
  onProgress: StreamCallback = () => undefined,
  onTool: ToolCallback = () => undefined
): Promise<T> => {
  let result: T | undefined;
  await readEventStream(stream, (event, payload) => {
    if (event === "progress" && typeof payload.stage === "string") onProgress(payload.stage);
    if (event === "tool") {
      const trace = asToolTrace(payload);
      if (trace) onTool(trace);
    }
    if (event === "result" && payload.result) result = payload.result as T;
    if (event === "error") throw new Error(payload.text || "访谈 Agent 请求失败");
  });
  if (!result) throw new Error("访谈 Agent 未返回有效结果，请重试");
  return result;
};
